import React, { useCallback, useEffect, useState } from "react";
import api, { formatEUR, formatApiErrorDetail } from "@/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

const dt = (s) => new Date(s).toLocaleString("es-ES");

export default function History() {
  const [sales, setSales] = useState([]);
  const [expenses, setExpenses] = useState([]);

  const load = useCallback(async () => {
    const [s, e] = await Promise.all([api.get("/sales"), api.get("/expenses")]);
    setSales(s.data);
    setExpenses(e.data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const deleteSale = async (id) => {
    try {
      await api.delete(`/sales/${id}`);
      toast.success("Venta eliminada");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const deleteExpense = async (id) => {
    try {
      await api.delete(`/expenses/${id}`);
      toast.success("Gasto eliminado");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="space-y-8" data-testid="history-page">
      <header>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">
          Historial
        </h1>
        <p className="text-slate-500 mt-1">
          Todas las ventas y gastos registrados.
        </p>
      </header>

      <Tabs defaultValue="sales" className="w-full">
        <TabsList data-testid="history-tabs">
          <TabsTrigger value="sales" data-testid="tab-sales">
            Ventas ({sales.length})
          </TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">
            Gastos ({expenses.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="sales" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Auto</th>
                    <th className="py-3 px-4">Extras</th>
                    <th className="py-3 px-4">Cliente</th>
                    <th className="py-3 px-4">Pago</th>
                    <th className="py-3 px-4 text-right">Total</th>
                    <th className="py-3 px-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((s) => (
                    <tr
                      key={s.id}
                      className="border-t border-slate-100"
                      data-testid={`sale-row-${s.id}`}
                    >
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{dt(s.date)}</td>
                      <td className="py-3 px-4 font-medium">{s.car_type_name}</td>
                      <td className="py-3 px-4 text-slate-600">
                        {s.extras?.length ? s.extras.map((e) => e.name).join(", ") : "—"}
                      </td>
                      <td className="py-3 px-4 text-slate-600">{s.customer_name || "—"}</td>
                      <td className="py-3 px-4 text-slate-600 capitalize">{s.payment_method}</td>
                      <td className="py-3 px-4 text-right font-semibold tabular-nums">
                        {formatEUR(s.total)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar venta?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {s.car_type_name} · {formatEUR(s.total)}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteSale(s.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))}
                  {sales.length === 0 && (
                    <tr>
                      <td colSpan={7} className="py-12 text-center text-slate-500">
                        Aún no hay ventas.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="expenses" className="mt-6">
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                    <th className="py-3 px-4">Fecha</th>
                    <th className="py-3 px-4">Descripción</th>
                    <th className="py-3 px-4">Categoría</th>
                    <th className="py-3 px-4 text-right">Importe</th>
                    <th className="py-3 px-4 text-right"></th>
                  </tr>
                </thead>
                <tbody>
                  {expenses.map((e) => (
                    <tr
                      key={e.id}
                      className="border-t border-slate-100"
                      data-testid={`expense-row-${e.id}`}
                    >
                      <td className="py-3 px-4 text-slate-600 whitespace-nowrap">{dt(e.date)}</td>
                      <td className="py-3 px-4 font-medium">{e.description}</td>
                      <td className="py-3 px-4 text-slate-600 capitalize">{e.category}</td>
                      <td className="py-3 px-4 text-right font-semibold tabular-nums text-red-700">
                        -{formatEUR(e.amount)}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="ghost" size="icon" className="text-red-600">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>¿Eliminar gasto?</AlertDialogTitle>
                              <AlertDialogDescription>
                                {e.description} · {formatEUR(e.amount)}
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction
                                onClick={() => deleteExpense(e.id)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Eliminar
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </td>
                    </tr>
                  ))}
                  {expenses.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-500">
                        Aún no hay gastos.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
